/* global Flex */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Field, formValueSelector, change, reset, reduxForm } from 'redux-form';
import { purchase as purchaseApi } from 'js-api-client';
import usePurchase from 'hooks/store/usePurchase';
import cybersource from 'payments/core/engines/cybersource';
import {
  fieldRenderer,
  selectRenderer,
  getExpirationMonths,
  getExpirationYears,
} from 'utils/formRenderers';
import { getBinFromCybersourceToken, getPreferredCybersourceCard } from 'utils/Cybersource';
import { trackEvent } from 'user-analytics';
import { DIGITAL_CARD_RECOMMENDATION_SHOWED } from 'constants/TrackEvents';
import CardFormPaymentOptions from 'components/purchase/CardFormPaymentOptions';
import InstallmentsPlanSelectorOv from 'components/purchase/InstallmentsPlanSelectorOv';
import cybersourceLogo from 'images/payment/cybersource.png';
import { Spacing, MessageBox, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import PaymentImage from '../../../ui/atoms/PaymentImage';
import useReactNativeMessage from '../../../hooks/useReactNativeMessage';
import 'styles/components/CybersourceForm';

/**
 * Validates the form fields for Cybersource payment.
 * @param {Object} values - Form values to be validated
 * @param {string} values.holderName - The holder name value to be validated
 * @param {string} values.expirationMonth - The expiration month value to be validated
 * @param {string} values.expirationYear - The expiration year value to be validated
 * @param {boolean} values.securityCodeError - The security code error flag
 * @param {boolean} values.cardNumberError - The card number error flag
 * @returns {Object} Errors object with error messages for each field
 */
const validate = ({
  holderName,
  expirationMonth,
  expirationYear,
  securityCodeError,
  cardNumberError,
}) => {
  const errors = {};

  if (cardNumberError) {
    errors.cardNumberError = 'Card Number error';
  }

  if (securityCodeError) {
    errors.securityCode = 'Security Code Error';
  }

  if (!holderName) {
    errors.holderName = 'Required';
  }

  if (!expirationMonth) {
    errors.expirationMonth = 'Invalid expiry';
  }
  if (!expirationYear) {
    errors.expirationYear = 'Invalid expiry';
  }

  return errors;
};

/**
 * Cybersource Form
 * @returns
 */
const CybersourceForm = () => {
  const {
    qualifiesForMonthlyInstallments,
    total,
    paymentPlans,
    monthlySelectedPlan,
    selectedInstallmentsPlan,
    paymentMethods,
  } = usePurchase();
  const selectedPaymentOption = useSelector(
    (state) => formValueSelector('card')(state, 'paymentPlan') || 'single',
  );
  const { env, features } = useSelector((state) => state.whitelabelConfig);
  const planSelectorVisible = selectedPaymentOption === 'installments' && monthlySelectedPlan === 1;
  let visaMcPlan = {};
  let maxMonths = 1;
  let maxInstallmentsPlan = {};
  const fieldHolderName = useRef();
  const fieldExpirationMonth = useRef();
  const fieldExpirationYear = useRef();
  const RNMessage = useReactNativeMessage();
  const dispatch = useDispatch();
  const { t } = useTranslation('payment');
  const cybersourceJWT = paymentMethods?.find((method) => method.engine === 'cybersource')?.extra
    ?.paymentGatewayToken?.jwt;
  const loadedCybersourceJWT = useRef();
  const cybersourceNumberField = useRef();
  const cybersourceSecurityCodeField = useRef();

  // Digital-card recommendation message (IAMSA has no installments; an eligible BIN shows a message)
  const [binMessageVisible, setBinMessageVisible] = useState(false);
  const binCheckedRef = useRef(false);
  const isMountedRef = useRef(true);
  // Bumped on every BIN check (and whenever inputs change); only the latest check may update state.
  const binCheckRequestIdRef = useRef(0);
  const cardSelector = formValueSelector('card');
  const cardNumberError = useSelector((state) => cardSelector(state, 'cardNumberError'));
  const securityCodeError = useSelector((state) => cardSelector(state, 'securityCodeError'));
  const expirationMonth = useSelector((state) => cardSelector(state, 'expirationMonth'));
  const expirationYear = useSelector((state) => cardSelector(state, 'expirationYear'));

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!cybersourceJWT || loadedCybersourceJWT.current === cybersourceJWT) return undefined;

    // Flex throws "Field is not loaded. Call \"load\" before attempting to unload." when a hosted
    // field is torn down before its async load() completes (e.g. the form unmounts because the
    // wallet covers the full total and the payment method switches to reservamos_pay). The field is
    // already going away, so swallow that error instead of letting it crash the checkout.
    // eslint-disable-next-line jsdoc/require-jsdoc
    const safeUnloadField = (field) => {
      try {
        field?.unload?.();
      } catch (error) {
        // no-op: field was not loaded yet
      }
    };

    // eslint-disable-next-line jsdoc/require-jsdoc
    const unloadCybersourceFields = () => {
      safeUnloadField(cybersourceNumberField.current);
      safeUnloadField(cybersourceSecurityCodeField.current);
      cybersourceNumberField.current = null;
      cybersourceSecurityCodeField.current = null;
      loadedCybersourceJWT.current = undefined;
    };

    // eslint-disable-next-line jsdoc/require-jsdoc
    const initializeCybersourceFields = () => {
      if (loadedCybersourceJWT.current === cybersourceJWT) return true;
      if (typeof Flex === 'undefined') return false;

      unloadCybersourceFields();

      cybersource.initializeFlex(cybersourceJWT);
      const microform = cybersource.initializeMicroform();
      const number = microform.createField('number', {
        placeholder: t('card_number'),
      });
      const securityCode = microform.createField('securityCode', {
        placeholder: t('card_CVV'),
      });

      number.load('#number-container');

      number.on('change', function handleNumberChange(data) {
        dispatch(change('card', 'cardNumberError', !data.valid));
        const card = getPreferredCybersourceCard(data.card);
        if (card) {
          dispatch(change('card', 'cardBrand', card.name));
        }
      });

      securityCode.load('#securityCode-container');

      securityCode.on('change', function handleSecurityCodeChange(data) {
        dispatch(change('card', 'securityCodeError', !data.valid));
      });

      cybersourceNumberField.current = number;
      cybersourceSecurityCodeField.current = securityCode;
      loadedCybersourceJWT.current = cybersourceJWT;
      return true;
    };

    const flexScript = document.getElementById('cybersource-flex');

    // eslint-disable-next-line jsdoc/require-jsdoc
    const handleFlexLoad = () => {
      initializeCybersourceFields();
    };

    if (!initializeCybersourceFields() && flexScript) {
      flexScript.addEventListener('load', handleFlexLoad);
    }

    return () => {
      flexScript?.removeEventListener('load', handleFlexLoad);
      unloadCybersourceFields();
    };
  }, [cybersourceJWT, dispatch, t]);

  useEffect(() => {
    RNMessage.post({ formLoaded: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * When enabled, check the card BIN (obtained from a transient token, since Cybersource hosted
   * fields never expose raw digits) and show the digital-card recommendation message if eligible.
   */
  useEffect(() => {
    if (!features.SHOW_DIGITAL_CARD_RECOMMENDATION) return;

    // The BIN comes from a transient token (Cybersource hosted fields never expose raw digits), and
    // createToken needs the hosted number + security-code fields valid plus a real expiration. The
    // holder name is NOT required. This token is only used to read the BIN and is discarded; payment
    // creates its own at submit.
    const readyToCheck =
      !cardNumberError && !securityCodeError && !!expirationMonth && !!expirationYear;

    if (readyToCheck && !binCheckedRef.current) {
      binCheckedRef.current = true;

      binCheckRequestIdRef.current += 1;
      const requestId = binCheckRequestIdRef.current;
      // True only while this is still the most recent check and the component is mounted.
      // eslint-disable-next-line jsdoc/require-jsdoc
      const isLatest = () => isMountedRef.current && requestId === binCheckRequestIdRef.current;

      // eslint-disable-next-line jsdoc/require-jsdoc
      const checkDigitalCardRecommendation = async () => {
        try {
          const microform = cybersource.getMicroformInstance();
          if (!microform) return;

          const token = await new Promise((resolve, reject) => {
            microform.createToken({ expirationMonth, expirationYear }, (error, createdToken) => {
              if (error) {
                reject(error);
                return;
              }
              resolve(createdToken);
            });
          });

          const bin = getBinFromCybersourceToken(token);
          if (!bin) {
            if (isLatest()) setBinMessageVisible(false);
            return;
          }

          const response = await purchaseApi.validateInstallments(
            { bin },
            { engine: 'cybersource' },
          );
          if (!isLatest()) return;

          setBinMessageVisible(response.eligible ?? false);
          if (response.eligible) {
            trackEvent(DIGITAL_CARD_RECOMMENDATION_SHOWED, { Engine: 'cybersource', BIN: bin });
          }
        } catch (error) {
          console.error('Error checking Cybersource digital-card recommendation:', error);
          if (isLatest()) setBinMessageVisible(false);
        }
      };

      checkDigitalCardRecommendation();
    } else if (cardNumberError || securityCodeError) {
      // The number or CVV is being edited; reset so the BIN is re-checked once both are valid
      // again, and hide the message meanwhile. Bump the request id so any in-flight check is ignored.
      binCheckRequestIdRef.current += 1;
      binCheckedRef.current = false;
      setBinMessageVisible(false);
    }
  }, [
    features.SHOW_DIGITAL_CARD_RECOMMENDATION,
    cardNumberError,
    securityCodeError,
    expirationMonth,
    expirationYear,
  ]);

  /**
   * Select the installments plan and update the form accordingly
   * @param {Object} installmentsPlan - The selected installments plan
   */
  const selectInstallmentsPlan = (installmentsPlan) => {
    dispatch(selectInstallmentsPlan(installmentsPlan));
    dispatch(change('card', 'monthlySelectedPlan', installmentsPlan.months));
  };

  /**
   * Reset the selected installments plan and form fields
   */
  const resetInstallmentsPlan = () => {
    dispatch(resetInstallmentsPlan());
    dispatch(change('card', 'paymentPlan', 'single'));
    dispatch(reset('installmentsSelector'));
  };

  /**
   * Select the installments plan and update the form accordingly
   * @param {string} selectedCard - The selected card type
   * @param {number} selectedMonths - The selected number of months
   */
  const onSelectPlan = (selectedCard, selectedMonths) => {
    selectInstallmentsPlan({
      ...paymentPlans[selectedCard][selectedMonths],
      card: selectedCard,
      months: selectedMonths,
    });
  };

  if (qualifiesForMonthlyInstallments) {
    visaMcPlan = paymentPlans.visaMc;
    maxMonths = Number(Math.max(...Object.keys(visaMcPlan)));
    maxInstallmentsPlan = { ...visaMcPlan[maxMonths], months: maxMonths };
  }

  const monthsOptions = getExpirationMonths().map((option) => ({
    value: option.value,
    label: option.value,
  }));
  const yearsOptions = getExpirationYears().map((year) => ({ value: year, label: year }));

  // TODO: Agregar validacion si no se inicializa cybersource, mostrar algo en el componente - info - dialog

  return (
    <>
      <div className="card-form">
        <CardFormPaymentOptions
          qualifiesForMonthlyInstallments={qualifiesForMonthlyInstallments}
          monthlySelectedPlan={monthlySelectedPlan}
          selectedInstallmentsPlan={selectedInstallmentsPlan}
          maxInstallmentsPlan={maxInstallmentsPlan}
          total={total}
          onChangeClick={resetInstallmentsPlan}
        />

        <Field type="hidden" name="cardNumberError" component="input" />
        <Field type="hidden" name="securityCodeError" component="input" />
        <Field type="hidden" name="monthlySelectedPlan" component="input" />

        <div className="checkout-section">
          <Spacing vertical size="XS">
            {features.BILLING_ADDRESS_ENABLED && (
              <Field
                id="billingAddress"
                type="text"
                name="billingAddress"
                placeholder={t('billing_address')}
                component={fieldRenderer}
              />
            )}
            <label className="flex-label" htmlFor="number-container">
              {t('card_number')}
            </label>
            <div id="number-container" className="form-control" />

            <p className="heading-form-text">{t('card_expiration')}</p>
            <Spacing flexGrow>
              <Field
                ref={fieldExpirationMonth}
                id="expirationMonth"
                name="expirationMonth"
                cls="form-item-wrap-l fs-exclude"
                component={selectRenderer}
                defaultValue={t('card_month')}
                options={monthsOptions}
                placeholder={t('card_month')}
              />

              <Field
                id="expirationYear"
                ref={fieldExpirationYear}
                name="expirationYear"
                component={selectRenderer}
                options={yearsOptions}
                placeholder={t('card_year')}
              />
            </Spacing>
            <label className="flex-label" htmlFor="securityCode-container">
              {t('card_CVV')}
            </label>
            <Spacing alignItems="end">
              <div id="securityCode-container" className="form-control" />

              <PaymentImage type="cvv" />
            </Spacing>
            <Field
              ref={fieldHolderName}
              id="holderName"
              type="text"
              name="holderName"
              placeholder={t('card_owner_name')}
              component={fieldRenderer}
            />

            {env.cybersource && env.cybersource.enabled ? (
              <Spacing justifyContent="flex-end">
                <img src={cybersourceLogo} height="25px" width="110px" />
              </Spacing>
            ) : null}
          </Spacing>
        </div>

        <InstallmentsPlanSelectorOv
          visible={planSelectorVisible}
          paymentPlans={paymentPlans}
          onSelectPlan={onSelectPlan}
        />

        {features.SHOW_DIGITAL_CARD_RECOMMENDATION && binMessageVisible && (
          <MessageBox borderColor="info">
            <Text size="S">{t('digital_card_recommendation')}</Text>
          </MessageBox>
        )}
      </div>
    </>
  );
};

export default reduxForm({
  form: 'card',
  validate,
  initialValues: {
    securityCodeError: true,
    cardNumberError: true,
  },
})(CybersourceForm);
